import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../../../api/queryClient';
import { ServiceAgreementStatusEnum } from '../../../api/generated/pm';
import {
  getV1ProvidersOptions,
  getV1ServiceAgreementsWithProviderOptions,
  getV1ServiceAgreementsWithProviderQueryKey,
} from '../../../api/generated/pm/@tanstack/react-query.gen';

/**
 * Query hooks for the dealer side of Service Agreements: searching Providers
 * and listing the current Business Partner's agreements with them. See
 * `../IdentityProviders/identityProviders.queries.ts` for the pattern.
 */

export type getServiceAgreementParams = {
  page: string;
  limit: string;
  statuses?: ServiceAgreementStatusEnum[];
  name?: string;
};

export function useGetServiceAgreementsWithProvider(
  params: getServiceAgreementParams,
  enabled: boolean = true
) {
  const { statuses, name } = params;
  const normalizedStatuses = [...(statuses ?? [])].sort();
  const trimmedName = (name ?? '').trim();
  return useQuery({
    ...getV1ServiceAgreementsWithProviderOptions({
      query: {
        page: 1,
        limit: 1000,
        ...(normalizedStatuses.length && {
          'filter[serviceAgreementStatus]': { in: normalizedStatuses.join(',') },
        }),
        ...(trimmedName && { 'filter[customer.name]': { startswith: trimmedName } }),
      },
    }),
    enabled,
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
  });
}

/** Query key prefix for every filter variant of the dealer-side SA list. */
export const SERVICE_AGREEMENTS_WITH_PROVIDER_QUERY_KEY =
  getV1ServiceAgreementsWithProviderQueryKey();

/** Clears every filter variant of the dealer-side Service Agreement list. */
export const invalidateServiceAgreementsWithProvider = () =>
  queryClient.invalidateQueries({ queryKey: SERVICE_AGREEMENTS_WITH_PROVIDER_QUERY_KEY });

export interface AvailableAgreementsFilters {
  name?: string;
  postalCode?: string;
  country?: string;
}

/** Providers the current Business Partner could enter a Service Agreement with. */
export const useAvailableAgreements = (filters: AvailableAgreementsFilters) => {
  const { name, country, postalCode } = filters;

  return useQuery(
    getV1ProvidersOptions({
      query: {
        page: 1,
        limit: 1000,
        ...(name && { 'filter[name]': { startswith: name } }),
        ...(postalCode && { 'filter[companyAddress.zipPostalCode]': { eq: postalCode } }),
        ...(country && { 'filter[companyAddress.country]': { eq: country } }),
      },
    })
  );
};
